import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import AgentDomain from "entities/domain/agents/agent-domain";
import MerchantAgentDomain from "entities/domain/merchant-agents/merchant-agent-domain";
import {
  ConversationsListParams,
  conversationsListQueryKey,
  fetchConversationsListPage,
} from "hooks/queries/conversations-list";
import { useCurrentAgent } from "hooks/queries/use-current-agent";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useMerchantAgents } from "hooks/queries/use-teammates";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  ConversationTab,
  filterAgentsSelector,
  getActiveTabFromLocalStorage,
  getInboxScopeFromLocalStorage,
  getLegacyTeamScopeFromLocalStorage,
  filterChannelsSelector,
  filterCustomerTagIdsSelector,
  filterSentimentSelector,
  filterUrgencySelector,
  openClosedSelector,
  searchFieldSelector,
  searchTextSelector,
  selectedInboxSelector,
  sortOrderSelector,
} from "redux/features/conversations";
import { useAppSelector } from "redux/hooks";
import { canManageTeamInbox } from "util/permissions";
import {
  ALL_INBOXES_SCOPE,
  InboxScope,
  parseInboxScopeFromSearch,
  searchNamesInboxScope,
  serializeInboxScope,
} from "util/inbox-scope";

// Inbox-hosting pathnames: "/inbox", "/:merchantId/inbox[/...]" and the bare
// "/:merchantId[/]" root the app lands on after merchant resolution.
const INBOX_PATHNAME_PATTERN = "^(?:/\\d+)?/inbox(?:/.*)?$|^/\\d+/?$";

const MERCHANT_ID_PATHNAME_PATTERN = "^/(\\d+)(?:/|$)";

export const isInboxPathname = (pathname: string): boolean =>
  new RegExp(INBOX_PATHNAME_PATTERN).test(pathname);

const parseMerchantIdFromPathname = (pathname: string): number | undefined => {
  const match = new RegExp(MERCHANT_ID_PATHNAME_PATTERN).exec(pathname);
  return match ? parseInt(match[1], 10) : undefined;
};

const parseTabFromSearch = (search: string): ConversationTab | undefined => {
  const tab = new URLSearchParams(search).get("tab");
  return Object.values(ConversationTab).includes(tab as ConversationTab)
    ? (tab as ConversationTab)
    : undefined;
};

// Mirrors the default-tab decision in DesktopTabsArea/MobileTabsArea for URLs
// without an explicit ?tab= parameter.
const deriveDefaultTab = (
  merchantId: number | undefined,
  currentAgent: AgentDomain | undefined,
  agents: MerchantAgentDomain[]
): ConversationTab | undefined => {
  if (!merchantId || !currentAgent || !agents.length) {
    return undefined;
  }

  const isSingleAgent = agents.length === 1;
  const isManager = canManageTeamInbox(merchantId, currentAgent);

  return isManager && isSingleAgent
    ? ConversationTab.Team
    : ConversationTab.Personal;
};

// Same spirit as deriveDefaultTab: a guess, not the decision. The URL wins,
// then whatever the TabsArea has already resolved into Redux, then the
// per-device choice. The TabsArea supersedes this once its data loads.
//
// The localStorage read happens here rather than in the render body: this hook
// runs on every PrivateRoute render, on every route, and getItem is synchronous.
// Storage only changes when the user picks a scope, which also moves the URL,
// so nothing is missed by reading it inside the effect.
//
// `null` means no guess: a Release 1 link or saved Team choice only becomes an
// Inbox once the Inbox list is known, and guessing All would start the one
// request the list must not make.
const guessInboxScope = (
  search: string,
  selectedInbox: InboxScope
): InboxScope | null => {
  // Deliberately not resolved against the merchant's Inboxes. This is the LCP
  // guess, fired before the Inbox list has necessarily loaded, and taking that
  // dependency would put a request in front of the one this exists to start
  // early. A guess the render path later collapses to All costs one extra list
  // request and nothing else — useInboxScope is the authoritative read.
  if (searchNamesInboxScope(search)) {
    return parseInboxScopeFromSearch(search);
  }

  if (selectedInbox.kind !== "all") {
    return selectedInbox;
  }

  const stored = getInboxScopeFromLocalStorage();

  if (stored) {
    return stored;
  }

  return getLegacyTeamScopeFromLocalStorage() ? null : ALL_INBOXES_SCOPE;
};

// Fires the first page of the inbox conversations list as early as possible —
// from the eager app shell, as soon as auth is ready and the merchant id and
// tab are derivable from the URL — instead of waiting for the lazy chat chunk
// to load and mount ConversationSnippetList (~1.5s later on a throttled
// mobile load). The query key and fetcher are shared with
// useConversationsInfinite, so the list picks the prefetched page straight
// from the cache.
const usePrefetchConversations = () => {
  const auth0Context = useAuth0();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { data: merchant } = useMerchant();
  const { data: currentAgent } = useCurrentAgent();
  const { data: agents = [] } = useMerchantAgents(merchant?.id);

  const isOpenOrClosed = useAppSelector(openClosedSelector);
  const searchText = useAppSelector(searchTextSelector);
  const searchField = useAppSelector(searchFieldSelector);
  const filterAgents = useAppSelector(filterAgentsSelector);
  const filterChannels = useAppSelector(filterChannelsSelector);
  const filterCustomerTagIds = useAppSelector(filterCustomerTagIdsSelector);
  const filterSentiment = useAppSelector(filterSentimentSelector);
  const filterUrgency = useAppSelector(filterUrgencySelector);
  const sortOrder = useAppSelector(sortOrderSelector);
  const selectedInbox = useAppSelector(selectedInboxSelector);

  const merchantId =
    parseMerchantIdFromPathname(location.pathname) ?? merchant?.id;
  // Prefer the URL, then the persisted last-used tab (available immediately,
  // no API round-trips), and only fall back to deriving the default from
  // merchant + agents data. A stale guess costs one extra list request; the
  // list itself always follows the tab the TabsArea resolves.
  const tab =
    parseTabFromSearch(location.search) ??
    getActiveTabFromLocalStorage() ??
    deriveDefaultTab(merchantId, currentAgent, agents);
  // Deps key on the two inputs that are free to read; the stored fallback is
  // resolved inside the effect.
  const scopeFromUrl = parseInboxScopeFromSearch(location.search);
  const urlOrReduxScopeKey = searchNamesInboxScope(location.search)
    ? scopeFromUrl && serializeInboxScope(scopeFromUrl)
    : serializeInboxScope(selectedInbox);

  useEffect(() => {
    if (!auth0Context.isAuthenticated) {
      return;
    }

    if (!isInboxPathname(location.pathname) || !merchantId || !tab) {
      return;
    }

    const inboxScope = guessInboxScope(location.search, selectedInbox);

    if (inboxScope === null) {
      return;
    }

    const params: Omit<ConversationsListParams, "merchantId" | "offset"> = {
      tab,
      status: isOpenOrClosed,
      searchText,
      searchField,
      channels: filterChannels,
      agents: filterAgents,
      tagIds: filterCustomerTagIds,
      inboxScope,
      sentiment: filterSentiment,
      urgency: filterUrgency,
      sortOrder,
    };

    // Respects staleTime: repeat runs within the window are no-ops, and an
    // in-flight fetch is shared with the list's useInfiniteQuery.
    queryClient.prefetchInfiniteQuery({
      queryKey: conversationsListQueryKey(merchantId, params),
      queryFn: async ({ pageParam = 0, signal }) =>
        fetchConversationsListPage(
          auth0Context,
          merchantId,
          params,
          pageParam as number,
          signal
        ),
      initialPageParam: 0,
      staleTime: 60_000,
    });
  }, [
    auth0Context.isAuthenticated,
    location.pathname,
    merchantId,
    tab,
    // The scope object is rebuilt every render; depend on its serialization so
    // an unchanged scope does not re-run the prefetch.
    urlOrReduxScopeKey,
    isOpenOrClosed,
    searchText,
    searchField,
    filterChannels,
    filterAgents,
    filterCustomerTagIds,
    filterSentiment,
    filterUrgency,
    sortOrder,
    queryClient,
  ]);
};

export default usePrefetchConversations;
